import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const CameraCapture = ({ onCapture, capturedImage }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [facingMode, setFacingMode] = useState('user');

  useEffect(() => {
    if (!capturedImage) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [facingMode, capturedImage]);

  const startCamera = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const mediaStream = await navigator.mediaDevices?.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err?.name === 'NotAllowedError') {
        setError('Camera access denied. Please enable camera permissions in your browser settings.');
      } else if (err?.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Unable to access camera. Please check your device settings.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef?.current || !canvasRef?.current) return;

    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    canvas.width = video?.videoWidth;
    canvas.height = video?.videoHeight;

    const context = canvas?.getContext('2d');
    context?.drawImage(video, 0, 0, canvas?.width, canvas?.height);

    canvas?.toBlob((blob) => {
      const imageUrl = URL.createObjectURL(blob);
      onCapture(imageUrl, blob);
      stopCamera();
    }, 'image/jpeg', 0.95);
  };

  const retakePhoto = () => {
    onCapture(null, null);
    startCamera();
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  if (error) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-2 p-4 md:p-6 border border-border">
        <div className="flex flex-col items-center justify-center py-8 md:py-12">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-error/10 flex items-center justify-center mb-4">
            <Icon name="CameraOff" size={32} className="md:w-10 md:h-10 text-error" />
          </div>
          <p className="text-sm md:text-base text-error text-center mb-4 px-4">{error}</p>
          <Button variant="outline" onClick={startCamera} iconName="RefreshCw" iconPosition="left">
            Retry Camera Access
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 p-4 md:p-6 border border-border">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Camera" size={20} className="md:w-6 md:h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold text-foreground">Capture Selfie</h2>
            <p className="text-xs md:text-sm text-muted-foreground">Take a clear photo for verification</p>
          </div>
        </div>
      </div>

      <div className="relative bg-muted rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
        {capturedImage ? (
          <img
            src={capturedImage}
            alt="Captured selfie for punch out verification"
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm md:text-base text-foreground">Starting camera...</p>
                </div>
              </div>
            )}
          </>
        )}

        <canvas ref={canvasRef} className="hidden" />

        {!capturedImage && !isLoading && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 md:gap-4">
            <button
              onClick={switchCamera}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-all shadow-elevation-2"
              aria-label="Switch camera"
            >
              <Icon name="SwitchCamera" size={20} className="md:w-6 md:h-6" />
            </button>
            <button
              onClick={capturePhoto}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center transition-all shadow-elevation-3"
              aria-label="Capture photo"
            >
              <Icon name="Camera" size={28} className="md:w-8 md:h-8 text-white" />
            </button>
          </div>
        )}
      </div>

      {capturedImage && (
        <div className="mt-4 flex gap-3">
          <Button variant="outline" onClick={retakePhoto} iconName="RotateCcw" iconPosition="left" fullWidth>
            Retake Photo
          </Button>
        </div>
      )}
    </div>
  );
};

export default CameraCapture;


